import { Box, IconButton, TextField, Typography } from "@mui/material";
import { Modal, ModalBody } from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { UsernameSelectField, styles } from "../../pages/MyTracks/styles";
import { useStyles } from "../../pages/MyTracks/styles";
import {
  GetEnterpriseAllUsersResponse,
  AddCollaboratorsToProjectsResponse,
} from "../../proto/api_pb";
import { Collaborator, User } from "../../proto/api_pb";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext, UserDetailsContext } from "../../contexts";
import { UserDetailsContextInterface } from "../../contexts";
import { RpcError } from "grpc-web";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { collaboratorsModalStyles, shareModalStyles } from "./styles";
import {
  APP_BACKGROUND_COLOR,
  APP_COLOR,
  FONT_FAMILY,
  TEXT_COLOR,
} from "../../theme";
import { getEnterpriseAllUsers } from "../../services/enterpriseService";
import { addCollaboratorsToProjects } from "../../services/projectService";
import CustomButton from "../CustomButton/CustomButton";

interface AddCollabsToProjectsModalProps {
  open: boolean;
  onClose: () => void;
  selectedProjectIds: Set<string>;
  onSuccess: () => void;
}

const AddCollabsToProjectsModal: React.FC<AddCollabsToProjectsModalProps> = ({
  open,
  onClose,
  selectedProjectIds,
  onSuccess,
}) => {
  const classes = useStyles();
  const [collabVal, setCollabVal] = useState("");
  const [usersList, setUsersList] = useState<Array<User>>([]);
  const { userName } = useContext<UserDetailsContextInterface>(UserDetailsContext);
  const { userId } = useContext(AuthContext);
  const [collaborators, setCollaborators] = useState<Array<Collaborator>>([]);
  const { getValidAccessToken } = useGetValidAccessToken();
  const [loading, setLoading] = useState<boolean>(false);
  const [userNames, setUserNames] = useState<string[]>([]);
  const { enterprise } = useContext(UserDetailsContext);

  useEffect(() => {
    if (open) {
      setCollaborators([]);
      setCollabVal("");
    }
  }, [open]);

  const addCollaborator = (newCollaborator: string) => {
    if (newCollaborator) {
      if (
        collaborators.find(
          (collaborator) => collaborator.getUsername() === newCollaborator
        ) === undefined &&
        usersList.some((user) => user.getUsername() === newCollaborator)
      ) {
        const addedCollab = usersList.find(
          (user) => user.getUsername() === newCollaborator
        );

        if (addedCollab) {
          const newCollab = new Collaborator();
          newCollab.setUserId(addedCollab?.getUserId());
          newCollab.setUsername(addedCollab?.getUsername());
          newCollab.setProfilePicture(addedCollab.getProfilePicture());
          setCollaborators([...collaborators, newCollab]);
        }
        setCollabVal("");
      } else {
        toast.info("Username does not exist or already added");
      }
    }
  };

  const removeCollaborator = (collaborator: Collaborator) => {
    const updatedCollaborators = collaborators.filter(
      (item) => item.getUserId() !== collaborator.getUserId()
    );
    setCollaborators(updatedCollaborators);
  };

  const handleConfirm = async () => {
    if (collaborators.length === 0) {
      toast.error("Please add at least one collaborator");
      return;
    }
    if (selectedProjectIds.size === 0) {
      toast.error("No projects selected");
      return;
    }

    try {
      setLoading(true);
      const token = await getValidAccessToken();
      const projectIds = Array.from(selectedProjectIds);

      const response = await addCollaboratorsToProjects(
        token,
        projectIds,
        collaborators,
        enterprise?.id ?? ""
      );

      if (response instanceof RpcError) {
        toast.error("Failed to add collaborators");
        throw response;
      }

      if (response instanceof AddCollaboratorsToProjectsResponse) {
        const updated = response.getUpdatedProjectIdsList();
        const failed = response.getFailedProjectIdsList();

        if (failed.length > 0) {
          toast.warning(`Updated ${updated.length} projects, ${failed.length} failed`);
        } else {
          toast.success(`Collaborators added to ${updated.length} projects`);
        }
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleUserNames = async () => {
      const token = await getValidAccessToken();
      const res = await getEnterpriseAllUsers(token, enterprise?.id ?? "");
      if (res instanceof GetEnterpriseAllUsersResponse) {
        const uName = res
          .getUsersList()
          .filter(
            (user) =>
              !user.getUsername()?.includes("dummy_ommuse") &&
              user.getUsername() !== userName
          )
          .map((user) => user.getUsername() || user.getEmail());
        setUserNames(uName);
        setUsersList(
          res
            .getUsersList()
            .filter(
              (user) =>
                !user.getUsername()?.includes("dummy_ommuse") &&
                user.getUsername() !== userName
            )
        );
      }
    };
    if (open) {
      handleUserNames();
    }
  }, [enterprise?.id, getValidAccessToken, userName, open]);

  return (
    <Modal
      isOpen={open}
      toggle={onClose}
      style={{ marginTop: 100 }}
    >
      <ModalBody style={{ backgroundColor: APP_BACKGROUND_COLOR }}>
        <Box sx={shareModalStyles.shareIconContainer}>
          <img
            style={shareModalStyles.shareIcons}
            src="/assets/icons/TracksPageIcons/users-plus.svg"
            alt="icon"
          />
        </Box>
        <Typography sx={{ color: TEXT_COLOR, marginBottom: "20px" }}
          variant="h6"
          fontFamily={FONT_FAMILY}
        >
          Add Collaborators to {selectedProjectIds.size} Project{selectedProjectIds.size !== 1 ? 's' : ''}
        </Typography>
        <IconButton
          onClick={onClose}
          style={styles.postMessageModalIcon}
        >
          <CloseIcon style={{ color: APP_COLOR }} />
        </IconButton>

        <Box sx={collaboratorsModalStyles.userNameLabelContainer}>
          <Box sx={{ width: "80%" }}>
            <label
              htmlFor="newCollaboratorUser"
              style={styles.modalCreatorUsername}
            >
              {"Collaborator's Username"}
            </label>
            <UsernameSelectField
              freeSolo
              forcePopupIcon={false}
              isOptionEqualToValue={(option, value) => option === value}
              inputValue={collabVal}
              onInputChange={(
                event: React.SyntheticEvent<Element, Event>,
                value: string
              ) => {
                setCollabVal(value.trim());
              }}
              options={userNames.filter(
                (name) => !collaborators.some((c) => c.getUsername() === name)
              )}
              renderInput={(params) => (
                <TextField
                  color="primary"
                  style={{
                    backgroundColor: APP_BACKGROUND_COLOR,
                    color: TEXT_COLOR,
                  }}
                  {...params}
                  label=" "
                  InputLabelProps={{
                    shrink: false,
                  }}
                  classes={{ root: classes.customTextField }}
                />
              )}
              renderOption={(props, option: any) => (
                <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                  {option}
                </li>
              )}
            />
          </Box>
          <CustomButton
            isDisabled={collabVal === ""}
            text="Add"
            onClick={() => addCollaborator(collabVal)}
            customStyles={collaboratorsModalStyles.customButtonStyles}
          />
        </Box>

        <Box style={collaboratorsModalStyles.collaboratorContainer}>
          <Box>
            {collaborators &&
              collaborators.length > 0 &&
              collaborators.map((collaborator, index) => (
                <Box
                  className="collaborator"
                  style={{ width: "100%" }}
                  key={index}
                >
                  <img
                    style={collaboratorsModalStyles.profilePic}
                    src={
                      collaborator.getProfilePicture()
                        ? collaborator.getProfilePicture()
                        : enterprise?.image
                    }
                    alt={collaborator.getUsername()}
                  />
                  <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1, color: TEXT_COLOR }}>
                    {collaborator.getUsername()}
                  </p>
                  <IconButton onClick={() => removeCollaborator(collaborator)}>
                    <DeleteForeverIcon />
                  </IconButton>
                </Box>
              ))}
          </Box>
        </Box>

        <CustomButton
          text="Confirm"
          loading={loading}
          onClick={handleConfirm}
          isDisabled={collaborators.length === 0}
          customStyles={{ width: "100%" }}
        />
      </ModalBody>
    </Modal>
  );
};

export default AddCollabsToProjectsModal;
